import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Tag, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const ErrorPageManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (vhostName) loadData();
    }, [vhostName]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getErrorPage(vhostName);
            if (response.success) {
                const items = Array.isArray(response.data) ? response.data : [];
                setDataSource(
                    items.map((item, index) => ({
                        key: item.code ?? item.name ?? index,
                        code: item.code ?? item.name ?? '',
                        page: item.page ?? item.value ?? '',
                    }))
                );
            } else {
                message.error(response.error || t('Failed to load error pages'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const response = await hostSharedService.addErrorPage(vhostName, values.code, values.page.trim());
            if (response.success) {
                message.success(t('Added successfully'));
                setModalVisible(false);
                loadData();
            } else {
                message.error(response.error || t('Failed to add'));
            }
        } catch (error) {
            if (!error.errorFields) message.error(t('Failed to add: {{message}}', { message: error.response?.data?.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (code) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteErrorPage(vhostName, code);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadData();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.response?.data?.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: t('Error code'), dataIndex: 'code', key: 'code', render: (text) => <Tag color="red">{text}</Tag> },
        { title: t('Error page'), dataIndex: 'page', key: 'page' },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Popconfirm title={t('Are you sure you want to delete?')} onConfirm={() => handleDelete(record.code)} okText={t('OK')} cancelText={t('Cancel')}>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>{t('Delete')}</Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalVisible(true); }}>
                    {t('Add error page')}
                </Button>
            </Space>
            <Table rowKey="code" columns={columns} dataSource={dataSource} loading={loading} pagination={{ pageSize: 10 }} />
            <Modal title={t('Add error page')} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} confirmLoading={loading}>
                <Form form={form} layout="vertical">
                    <Form.Item label={t('Error code')} name="code" rules={[{ required: true, message: t('Enter an error code') }, { type: 'number', min: 400, max: 599, message: t('Invalid error code') }]}>
                        <InputNumber placeholder={t('E.g. 404')} min={400} max={599} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label={t('Error page path')} name="page" rules={[{ required: true, whitespace: true, message: t('Enter a page path') }, { pattern: /^(\/[A-Za-z0-9._~/-]{1,250}|https?:\/\/\S{1,500})$/, message: t('Invalid error page path') }, { validator: (_, v) => (v && v.includes('..') ? Promise.reject(new Error(t('Invalid error page path'))) : Promise.resolve()) }]}>
                        <Input placeholder={t('E.g. /404.html')} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ErrorPageManager;
